// Maps between the IDP admin access-model wire shapes (src/api/access.types.ts)
// and the users-feature domain types — the single boundary where the wire
// shape is known; the Access tab and its hooks speak only domain types.
import type {
  AccessPreviewResponse,
  CatalogRoleResponse,
  ProductRolesResponse,
  RoleAssignmentResponse,
} from '@/api/access.types'
import type { AccessPreview, CatalogRole, RoleAssignment } from '../types'

// Re-exported from the shared wire-boundary module — the roles feature needs
// the same bridge-id predicate for its catalog-role list, so it lives beside
// access.types.ts rather than being owned by one feature. Kept re-exported
// here too so existing imports from this transformer (AccessTab, its spec)
// don't need to change.
export { isMigratedRoleId } from '@/api/access'

export function toCatalogRole(response: CatalogRoleResponse): CatalogRole {
  return {
    id: response.id,
    description: response.description,
    custom: response.custom,
    scopes: response.scopes,
    permissions: response.permissions,
    // `names` is absent on IDP builds that predate the names catalog.
    names: response.names ?? {},
  }
}

// GET /v1/admin/catalog/roles?product= responds with ProductRoles[] even when
// filtered to one product (0 or 1 matching entries) — flatten to the roles of
// the requested product only.
export function toCatalogRolesForProduct(response: ProductRolesResponse[], product: string): CatalogRole[] {
  return response.find((entry) => entry.product === product)?.roles.map(toCatalogRole) ?? []
}

export function toRoleAssignment(response: RoleAssignmentResponse): RoleAssignment {
  return { product: response.product, roleId: response.roleId }
}

export function toAccessPreview(response: AccessPreviewResponse): AccessPreview {
  return {
    scopes: response.scopes,
    roles: response.roles,
    permissions: response.permissions,
    resourceScope: {
      tenant: response.resourceScope.tenant,
      accounts: response.resourceScope.accounts,
    },
  }
}
